(() => {
  class StickyAddToCart {
    constructor(container) {
      this.container = container;
      this.section = container.closest('section');
      this.sectionId = container.dataset.sectionId;
      this.sourceButtonId = container.dataset.sourceButtonId;
      this.sourcePriceId = container.dataset.sourcePriceId;
      this.stickyButton = container.querySelector('[data-sticky-add-to-cart-button]');
      this.stickyButtonLabel = container.querySelector('[data-sticky-add-to-cart-label]');
      this.stickyPriceCurrent = container.querySelector('[data-sticky-add-to-cart-price-current]');
      this.stickyPriceCompare = container.querySelector('[data-sticky-add-to-cart-price-compare]');
      this.sourceButton = null;
      this.sourcePrice = null;
      this.intersectionObserver = null;
      this.sourceMutationObserver = null;
      this.priceMutationObserver = null;
      this.sectionMutationObserver = null;
      this.scheduledSync = false;

      if (!this.stickyButton) return;

      this.onIntersection = this.onIntersection.bind(this);
      this.onStickyButtonClick = this.onStickyButtonClick.bind(this);
      this.scheduleSync = this.scheduleSync.bind(this);

      this.stickyButton.addEventListener('click', this.onStickyButtonClick);
      this.observeSection();
      this.scheduleSync();
    }

    observeSection() {
      if (!this.section) return;

      this.sectionMutationObserver = new MutationObserver(this.scheduleSync);
      this.sectionMutationObserver.observe(this.section, { childList: true, subtree: true });
    }

    getSourceButton() {
      const buttonById = this.sourceButtonId ? document.getElementById(this.sourceButtonId) : null;
      if (buttonById) return buttonById;
      if (!this.sectionId) return null;

      return document.querySelector(`#MainProduct-${this.sectionId} #ProductSubmitButton-${this.sectionId}`);
    }

    getSourcePrice() {
      const priceById = this.sourcePriceId ? document.getElementById(this.sourcePriceId) : null;
      if (priceById) return priceById;
      if (!this.sectionId) return null;

      return document.querySelector(`#MainProduct-${this.sectionId} #price-${this.sectionId}`);
    }

    observeSourceButton(button) {
      if (this.sourceMutationObserver) this.sourceMutationObserver.disconnect();
      if (this.intersectionObserver) this.intersectionObserver.disconnect();

      if (!button) return;

      this.sourceMutationObserver = new MutationObserver(this.scheduleSync);
      this.sourceMutationObserver.observe(button, {
        attributes: true,
        childList: true,
        subtree: true,
        characterData: true
      });

      this.intersectionObserver = new IntersectionObserver(this.onIntersection, { threshold: 0 });
      this.intersectionObserver.observe(button);
    }

    observeSourcePrice(priceElement) {
      if (this.priceMutationObserver) this.priceMutationObserver.disconnect();
      if (!priceElement) return;

      this.priceMutationObserver = new MutationObserver(this.scheduleSync);
      this.priceMutationObserver.observe(priceElement, {
        attributes: true,
        childList: true,
        subtree: true,
        characterData: true
      });
    }

    scheduleSync() {
      if (this.scheduledSync) return;
      this.scheduledSync = true;

      window.requestAnimationFrame(() => {
        this.scheduledSync = false;
        this.sync();
      });
    }

    sync() {
      const nextSourceButton = this.getSourceButton();
      const nextSourcePrice = this.getSourcePrice();

      if (nextSourceButton !== this.sourceButton) {
        this.sourceButton = nextSourceButton;
        this.observeSourceButton(this.sourceButton);
      }

      if (nextSourcePrice !== this.sourcePrice) {
        this.sourcePrice = nextSourcePrice;
        this.observeSourcePrice(this.sourcePrice);
      }

      if (!this.sourceButton) {
        this.hide();
        this.stickyButton.disabled = true;
        return;
      }

      const sourceButtonLabel = this.sourceButton.querySelector('span')?.textContent?.trim();
      if (sourceButtonLabel && this.stickyButtonLabel) {
        this.stickyButtonLabel.textContent = sourceButtonLabel;
      }

      const isDisabled = this.sourceButton.disabled || this.sourceButton.getAttribute('aria-disabled') === 'true';
      this.stickyButton.disabled = isDisabled;
      this.stickyButton.classList.toggle('loading', this.sourceButton.classList.contains('loading'));
      this.syncPrice();
    }

    syncPrice() {
      if (!this.sourcePrice || !this.stickyPriceCurrent) return;

      const sourcePrice = this.sourcePrice.querySelector('.price');
      if (!sourcePrice) return;

      const regularPrice = sourcePrice.querySelector('.price__regular .price-item--regular')?.textContent?.trim();
      const salePrice = sourcePrice.querySelector('.price__sale .price-item--sale')?.textContent?.trim();
      const comparePrice = sourcePrice.querySelector('.price__sale .price-item--regular')?.textContent?.trim();
      const isOnSale = sourcePrice.classList.contains('price--on-sale') && salePrice;
      const activePrice = isOnSale ? salePrice : (regularPrice || salePrice);

      if (activePrice) this.stickyPriceCurrent.textContent = activePrice;

      if (!this.stickyPriceCompare) return;

      if (isOnSale && comparePrice) {
        this.stickyPriceCompare.textContent = comparePrice;
        this.stickyPriceCompare.classList.remove('hidden');
      } else {
        this.stickyPriceCompare.classList.add('hidden');
      }
    }

    onIntersection(entries) {
      const entry = entries[0];
      if (!entry || !this.sourceButton) return;

      const hasScrolledPastButton = entry.boundingClientRect.top < 0;
      const shouldShowStickyButton = hasScrolledPastButton && !entry.isIntersecting;
      this.container.classList.toggle('is-visible', shouldShowStickyButton);
      if (this.section) this.section.classList.toggle('product-sticky-atc-open', shouldShowStickyButton);
      this.container.hidden = !shouldShowStickyButton;
    }

    onStickyButtonClick(event) {
      event.preventDefault();
      if (!this.sourceButton || this.stickyButton.disabled) return;
      this.sourceButton.click();
    }

    hide() {
      this.container.hidden = true;
      this.container.classList.remove('is-visible');
      if (this.section) this.section.classList.remove('product-sticky-atc-open');
    }
  }

  const initStickyAddToCart = (scope = document) => {
    const stickyBars = scope.querySelectorAll('[data-sticky-add-to-cart]');
    stickyBars.forEach((stickyBar) => {
      if (stickyBar.dataset.stickyInitialized === 'true') return;
      stickyBar.dataset.stickyInitialized = 'true';
      new StickyAddToCart(stickyBar);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initStickyAddToCart());
  } else {
    initStickyAddToCart();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initStickyAddToCart(event.target);
  });
})();
